import {
  ChangeEvent,
  type FC,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { Dialog } from "@headlessui/react";
import {
  InformationCircleIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  useConfigurations,
  useUserConfigurations,
} from "@via/api/configuration.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component";
import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { WebsocketReconnectionOptions } from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-constants.ts";
import AppKindSelect from "@via/pages/apps/app-kind-select.tsx";
import AppResourcesFields from "@via/pages/apps/app-resources-fields.tsx";
import { useCreateApp } from "@via/pages/apps/apps.api.tsx";
import { PERMISSION, USER_ROLES } from "@via/utils/constants.tsx";
import {
  createAppOptions,
  createWorkspaceOptions,
} from "@via/utils/form-validators.tsx";
import { CreateApplicationRequestBody, Permissions } from "common";
import { useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { useAbility } from "@via/context/ability-context";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { useAuth } from "@via/hooks/useAuth.tsx";

interface CreateAppFormProps {
  setOpen: (open: boolean) => void;
}

const CreateAppForm: FC<CreateAppFormProps> = ({ setOpen }) => {
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const canModifyApp = user?.featureFlags?.FF_MODIFY_APPS;
  if (!isAdmin && !canModifyApp) {
    return null;
  }

  const [showOptional, setShowOptional] = useState(false);
  const { data: configurations } = useConfigurations();
  const { data: userConfigurations } = useUserConfigurations();
  // The App kind and Cluster resources fields ship dark (agent-9-1).
  const hpcAppsEnabled = Boolean(userConfigurations?.HPC_APPS_ENABLED);
  const defaultPermission = configurations?.ENABLE_SHARE_WITH_EVERYONE_APP
    ? Permissions.Public
    : Permissions.UserOwned;
  const {
    mutate: createApp,
    isPending: isCreateAppLoading,
    isSuccess: isCreateAppSuccess,
  } = useCreateApp();
  const ability = useAbility([
    {
      action: "Edit",
      subject: "Input",
      condition: configurations?.ENABLE_SHARE_WITH_EVERYONE_APP,
    },
  ]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateApplicationRequestBody>({
    mode: "onChange",
    defaultValues: {
      name: "",
      image: "",
      cmd: undefined,
      permission: defaultPermission,
      groupId: undefined,
      blankLaunch: false,
      convertJson: false,
      version: "1.0.0",
      appKind: null,
      appCpus: null,
      appMemoryMb: null,
      appWallMinutes: null,
    },
  });

  const permission = watch(PERMISSION);
  const appKind = watch("appKind");

  useEffect(() => {
    if (permission !== Permissions.GroupShared) {
      setValue("groupId", undefined); // Reset groupId when permission changes
    }
  }, [setValue, permission]);

  useEffect(() => {
    if (isCreateAppSuccess) {
      setOpen(false);
    }
  }, [isCreateAppSuccess, setOpen]);

  const handlePermissionChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const permission = e.target.value as unknown as Permissions;
    setValue(PERMISSION, permission);
  };

  const handleCreateApp = (payload: CreateApplicationRequestBody): void => {
    const updatedPayload = {
      ...payload,
      cmd: payload.cmd || undefined,
      env: payload.env || undefined,
      description: payload.description || undefined,
      volume: payload.volume || undefined,
      targetPath: payload.targetPath || undefined,
      relativeUrl: payload.relativeUrl || undefined,
      blankLaunch: payload.blankLaunch ?? undefined,
      revComment: undefined,
      port: payload.port
        ? parseInt(payload.port as unknown as string)
        : undefined,
    };
    createApp(updatedPayload);
  };

  // Function to render group selection based on permission
  const renderGroupSelection = (): ReactNode => {
    return permission?.toString() === Permissions.GroupShared.toString() ? (
      <UserGroupSelectComponent
        control={control}
        name="groupId"
        rules={createWorkspaceOptions.groupId}
        menuPlacement="top"
        errors={errors}
      />
    ) : null;
  };

  const onSubmit = handleSubmit(handleCreateApp);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              New App
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => setOpen(false)}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to create your new
              App.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="App Name"
                placeholder="Name your App"
                validations={createAppOptions.name}
              />
              <FormTextAreaComponent
                control={control}
                name="description"
                label="Description"
                placeholder="Enter a description for your app"
              />
              <FormInputComponent
                control={control}
                name="image"
                label="Container Image"
                placeholder="Specify the image location"
                validations={createAppOptions.image}
                showTooltipIcon
                tooltipText="Docker image to download, including optional tag (e.g., myorg/debrowser-app:latest)"
              />
              {/* <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Status
                </label>
                <div className="mt-2">
                  <SelectComponent
                    name="status"
                    defaultValue={AppStatusOptions[0]}
                    options={AppStatusOptions}
                    enableClear={false}
                  />
                </div>
              </div> */}
              <FormInputComponent
                control={control}
                name="version"
                label="App Version"
                validations={createAppOptions.version}
              />
              {hpcAppsEnabled && (
                <>
                  <AppKindSelect
                    value={appKind}
                    onChange={kind => setValue("appKind", kind)}
                  />
                  <AppResourcesFields
                    kind={appKind}
                    values={{
                      appCpus: watch("appCpus"),
                      appMemoryMb: watch("appMemoryMb"),
                      appWallMinutes: watch("appWallMinutes"),
                    }}
                    onChange={(field, value) => setValue(field, value)}
                  />
                </>
              )}
              <div className="flex flex-row items-center">
                <label className="mr-2 block text-sm font-medium leading-6 text-gray-900">
                  Show Optional Fields
                </label>
                <div onClick={() => setShowOptional(!showOptional)}>
                  {showOptional ? (
                    <MinusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
                  ) : (
                    <PlusCircleIcon className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-500" />
                  )}
                </div>
              </div>
              {showOptional && (
                <>
                  <label className="flex items-center gap-1 text-sm font-medium leading-6 text-gray-900">
                    <input
                      type="checkbox"
                      {...register("convertJson")}
                      className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    Convert App File to JSON File
                    <InformationCircleIcon
                      id="tooltip-convert-app"
                      className="h-5 w-5"
                    />
                  </label>
                  <Tooltip
                    anchorSelect="#tooltip-convert-app"
                    content="Check if you prefer to convert APP_FILE_URL and APP_FILE_METADATA_URL to JSON files before launching the app."
                  />
                  <label className="flex items-center gap-1 text-sm font-medium leading-6 text-gray-900">
                    <input
                      type="checkbox"
                      {...register("blankLaunch")}
                      className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    Launch in New Window
                    <InformationCircleIcon
                      id="tooltip-blank-launch"
                      className="h-5 w-5"
                    />
                  </label>
                  <Tooltip
                    anchorSelect="#tooltip-blank-launch"
                    content="Check if you prefer to launch the app in a new window."
                  />
                  <FormTextAreaComponent
                    control={control}
                    name="cmd"
                    label="Container Command"
                    placeholder={`R\n-e\nshinyproxy::run_01_hello()`}
                    showTooltipIcon
                    tooltipText="The command that will be run when the Docker container is launched;<br/>typically this command will be the R command as well as the command that will launch the Shiny app. <br/>Please enter each part of the command into a new line as in the example below:<br/>R<br/>-e<br/>shinyproxy::run_01_hello()"
                  />
                  <FormInputComponent
                    control={control}
                    name="port"
                    label="Container Port"
                    showTooltipIcon
                    tooltipText="The port on which the app is listening in the container; this setting will override the default port (8080)"
                  />
                  <FormInputComponent
                    control={control}
                    name="volume"
                    label="Container Volume"
                    showTooltipIcon
                    tooltipText="Docker volume to mount into the container."
                  />
                  <FormInputComponent
                    control={control}
                    name="targetPath"
                    label="Target Path"
                    showTooltipIcon
                    tooltipText="The (context) path on which the app is available. <br/>By default this is the root path (/) which suffices for most apps (especially Shiny apps)."
                  />
                  <div>
                    <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                      Websocket Reconnection Mode
                      <InformationCircleIcon
                        id="tooltip-websocket-reconnection"
                        className="h-5 w-5 ml-1"
                      />
                    </label>
                    <Tooltip
                      anchorSelect="#tooltip-websocket-reconnection"
                      style={{ zIndex: 999 }}>
                      <div>
                        <p>
                          ShinyProxy contains a mechanism for restoring
                          disturbed connections. The mode can be set to one of
                          None, Confirm or Auto.
                        </p>
                        <p>
                          In the first case, the mechanism is disabled and
                          ShinyProxy will not try to restore the connection with
                          the app.
                        </p>
                        <p>
                          Using Confirm, ShinyProxy will first ask the user for
                          confirmation before trying to restore the connection.
                        </p>
                        <p>
                          Using Auto, ShinyProxy will automatically try to
                          reconnect to the app without asking the user for
                          confirmation.
                        </p>
                      </div>
                    </Tooltip>
                    <div className="mt-2">
                      <SelectComponent
                        readOnly
                        name="websocketReconnectionMode"
                        defaultValue={WebsocketReconnectionOptions[0]}
                        options={WebsocketReconnectionOptions}
                        enableClear={false}
                      />
                    </div>
                  </div>
                  <FormTextAreaComponent
                    control={control}
                    name="env"
                    label="Container Environment"
                    placeholder={`key1:value1\nkey2:value2`}
                    showTooltipIcon
                    tooltipText="One or more environment variables specified as the following example:<br/>key1:value1<br/>key2:value2"
                  />
                  <FormInputComponent
                    control={control}
                    name="relativeUrl"
                    label="Relative URL"
                    showTooltipIcon
                    tooltipText="The remaining path given after the base URL.<br/> For example, /?jsonobject={{APP_FILE_URL}}&meta={{APP_FILE_METADATA_URL}}"
                  />
                  {/*<FormTextAreaComponent
                    control={control}
                    name="revComment"
                    label="Revision Comment"
                    placeholder="Enter a comment to describe changes if creating a revision"
                  />*/}
                </>
              )}
              <fieldset>
                <legend className="text-sm font-medium leading-6 text-gray-900">
                  Permission
                </legend>
                <div className="mt-2 space-y-4">
                  <div className="relative flex items-start">
                    <div className="absolute flex h-6 items-center">
                      <input
                        {...register(PERMISSION)}
                        id="permission-public"
                        onChange={handlePermissionChange}
                        value={Permissions.Public.toString()}
                        aria-describedby="permission-public-description"
                        type="radio"
                        disabled={!ability.can("Edit", "Input")}
                        checked={
                          permission?.toString() ===
                          Permissions.Public.toString()
                        }
                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                    <div
                      className={`pl-7 text-sm leading-6 ${!ability.can("Edit", "Input") ? "opacity-50" : ""}`}>
                      <label
                        htmlFor="permission-public"
                        className="font-medium text-gray-900">
                        Public access
                      </label>
                      <p
                        id="permission-public-description"
                        className="text-gray-500">
                        Everyone with the link will see this app.
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          {...register(PERMISSION)}
                          id="permission-group"
                          onChange={handlePermissionChange}
                          value={Permissions.GroupShared.toString()}
                          aria-describedby="permission-group-description"
                          type="radio"
                          checked={
                            permission?.toString() ===
                            Permissions.GroupShared.toString()
                          }
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-6">
                        <label
                          htmlFor="permission-group"
                          className="font-medium text-gray-900">
                          Private to group members
                        </label>
                        <p
                          id="permission-group-description"
                          className="text-gray-500">
                          Only members of this group would be able to access.
                        </p>
                      </div>
                    </div>
                  </div>
                  {renderGroupSelection()}
                  <div>
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          {...register(PERMISSION)}
                          id="permission-private"
                          onChange={handlePermissionChange}
                          value={Permissions.UserOwned.toString()}
                          aria-describedby="permission-private-description"
                          type="radio"
                          checked={
                            permission?.toString() ===
                            Permissions.UserOwned.toString()
                          }
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-6">
                        <label
                          htmlFor="permission-private"
                          className="font-medium text-gray-900">
                          Private to you
                        </label>
                        <p
                          id="permission-private-description"
                          className="text-gray-500">
                          You are the only one able to access this app.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="app-create-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button
          id="app-create-save"
          type="submit"
          disabled={isCreateAppLoading}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateAppLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
};

export default CreateAppForm;
